import React from 'react';
import { VIEW_SERVICE_REQUEST_LABELS as L } from '../../constants/labels';
import type {
  ServiceRequestRole,
  ViewServiceRequestDetail,
} from '../../types/viewServiceRequestType';
import {
  formatRole,
  formatScheduledDate,
  formatScheduledTime,
  orDash,
} from './viewServiceRequestHelpers';
import { InfoField, SectionCard } from './viewServiceRequestUI';
import SRPlaceholderTab from './SRPlaceholderTab';

const TWO_COLUMN_GRID = 'minmax(0, 1fr) minmax(0, 1fr)';

interface SRTechnicianTabProps {
  detail: ViewServiceRequestDetail | undefined;
}

const ScheduleChip: React.FC<{ value: string }> = ({ value }) => (
  <span
    className="inline-flex items-center justify-center rounded-[8px] bg-[#F2F4F5] text-neutral-10"
    style={{
      fontSize: 14,
      lineHeight: '20px',
      fontWeight: 400,
      letterSpacing: 0.25,
      padding: '6px 12px',
    }}
  >
    {value}
  </span>
);

const ScheduleRange: React.FC<{ start: string; end: string }> = ({
  start,
  end,
}) => (
  <div className="flex items-center gap-2 flex-wrap">
    <ScheduleChip value={start} />
    <span className="text-on-surface-variant">–</span>
    <ScheduleChip value={end} />
  </div>
);

const SRTechnicianTab: React.FC<SRTechnicianTabProps> = ({ detail }) => {
  const assignedTo = detail?.assigned_to;
  const assignedBy = detail?.assigned_by;

  if (!assignedTo) {
    return (
      <SRPlaceholderTab
        sectionTitle={L.NAV_TECHNICIAN}
        message={L.EMPTY_TECHNICIAN_MESSAGE}
        testId="view-sr-technician-empty"
      />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Card 1 — Dane osoby przydzielonej (assigned person) */}
      <SectionCard
        title={L.SECTION_ASSIGNED_PERSON_DATA}
        testId="view-sr-section-technician-data"
      >
        <div
          className="grid gap-x-8 gap-y-6"
          style={{ gridTemplateColumns: TWO_COLUMN_GRID }}
        >
          <InfoField
            label={L.FIELD_TECHNICIAN_NAME}
            value={orDash(assignedTo.name)}
            testId="view-sr-field-technician-name"
          />
          <InfoField
            label={L.FIELD_ASSIGNED_PERSON_ID}
            value={orDash(assignedTo.user_id)}
            testId="view-sr-field-technician-id"
          />
          <InfoField
            label={L.FIELD_ROLE}
            value={formatRole(assignedTo.role as ServiceRequestRole)}
            testId="view-sr-field-technician-role"
          />
          <InfoField
            label={L.FIELD_TECHNICIAN_EMAIL}
            value={orDash(assignedTo.email)}
            testId="view-sr-field-technician-email"
          />
          <InfoField
            label={L.FIELD_TECHNICIAN_PHONE}
            value={orDash(assignedTo.phone_number)}
            testId="view-sr-field-technician-phone"
          />
        </div>
      </SectionCard>

      {/* Card 2 — Przydzielono przez (assigned by) */}
      <SectionCard
        title={L.SECTION_ASSIGNED_BY_DATA}
        testId="view-sr-section-assigned-by-data"
      >
        <div
          className="grid gap-x-8 gap-y-6"
          style={{ gridTemplateColumns: TWO_COLUMN_GRID }}
        >
          <InfoField
            label={L.FIELD_TECHNICIAN_NAME}
            value={orDash(assignedBy?.name)}
            testId="view-sr-field-assigned-by"
          />
          <InfoField
            label={L.FIELD_ROLE}
            value={formatRole(assignedBy?.role as ServiceRequestRole)}
            testId="view-sr-field-assigned-by-role"
          />
          <InfoField
            label={L.FIELD_TECHNICIAN_EMAIL}
            value={orDash(assignedBy?.email)}
            testId="view-sr-field-assigned-by-email"
          />
        </div>
      </SectionCard>

      {/* Card 3 — Szczegóły zaplanowanej wizyty (scheduled visit) */}
      <SectionCard
        title={L.SECTION_SCHEDULED_VISIT}
        testId="view-sr-section-scheduled-visit"
      >
        <div
          className="grid gap-x-8 gap-y-6"
          style={{ gridTemplateColumns: TWO_COLUMN_GRID }}
        >
          <div className="flex min-w-0 flex-col gap-2">
            <p
              className="text-bodySmall font-normal text-on-surface-variant"
              data-testid="view-sr-field-scheduled-date"
            >
              {L.FIELD_SCHEDULED_DATE}
            </p>
            <ScheduleRange
              start={formatScheduledDate(detail?.scheduled_start_at)}
              end={formatScheduledDate(detail?.scheduled_end_at)}
            />
          </div>
          <div className="flex min-w-0 flex-col gap-2">
            <p
              className="text-bodySmall font-normal text-on-surface-variant"
              data-testid="view-sr-field-scheduled-time"
            >
              {L.FIELD_SCHEDULED_TIME}
            </p>
            <ScheduleRange
              start={formatScheduledTime(detail?.scheduled_start_at)}
              end={formatScheduledTime(detail?.scheduled_end_at)}
            />
          </div>
        </div>
      </SectionCard>
    </div>
  );
};

export default SRTechnicianTab;
